import { useState } from 'react';
import DataTable from '../../components/DataTable/DataTable';
import SearchComponent from '../../components/DataTable/SearchComponent';
import { useUser } from '../../hooks/useUser';
import ActivationModal from '../../components/Modals/ActivationModal';
import { Column } from 'react-table';
import UserRegistrationPopup from './UserRegistrationPopup';
import UserEditPopup from './UserEditPopup';
import { IUser } from '../../types/user/userInterfaces';
import { roleLabel } from '../../constants/roleLabels';

const UserTable = () => {
  const {
    handlerSearchByType,
    handlerDeactivateUser,
    users,
    paginator,
    updatePageSize,
    pageSize,
    handlerAddUser,
    handleUpdateUser,
    fetchUserToEdit,
    isCreateModalOpen,
    isUpdateModalOpen,
    openCreateUserModal,
    handleCloseCreate,
    handleCloseUpdate,
  } = useUser();

  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    status: boolean;
  } | null>(null);

  // 📌 Función para manejar la activación/desactivación de usuario
  const handleToggleUserStatus = (user: { id: number; status: boolean }) => {
    setSelectedUser(user);
    setIsActivationModalOpen(true);
  };

  // 📌 Confirmar activación/desactivación
  const handleConfirmToggleStatus = () => {
    if (selectedUser) {
      handlerDeactivateUser({ id: selectedUser.id });
    }
    setIsActivationModalOpen(false);
  };

  // 📌 Cancelar activación/desactivación
  const handleCancelToggle = () => {
    setIsActivationModalOpen(false);
    setSelectedUser(null);
  };

  // 📌 Abrir modal de edición con datos del usuario seleccionado
  const handleEditUser = (user: IUser) => {
    fetchUserToEdit(user.id);
  };

  // 📌 Botón de agregar usuario
  const addButton = (
    <button
      className="inline-flex items-center rounded-md bg-primary px-4 py-2 text-white hover:bg-opacity-90"
      onClick={() => openCreateUserModal()}
    >
      + Nuevo Usuario
    </button>
  );

  // 📌 Acciones para la tabla
  const actions = [
    { key: 'edit', onClick: handleEditUser },
    { key: 'toggle', onClick: handleToggleUserStatus },
  ];

  interface Role {
    id: number;
    name: string;
  }

  // 📌 Columnas de la tabla
  const columns: Column<object>[] = [
    { Header: 'Nombre de usuario', accessor: 'username' },
    { Header: 'Correo electrónico', accessor: 'email' },
    {
      Header: 'Roles',
      accessor: 'roles',
      Cell: ({ value }) =>
        value.map((role: Role) => roleLabel(role.name)).join(', '),
    },
    {
      Header: 'Estado',
      accessor: 'status',
      Cell: ({ value }) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            value ? 'bg-green-500 text-white' : 'bg-red text-white'
          }`}
        >
          {value ? 'Activo' : 'Inactivo'}
        </span>
      ),
    },
    {
      Header: 'Bloqueado',
      accessor: 'locked',
      Cell: ({ value }) => (
        <span
          className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${
            value ? 'bg-red text-white' : 'bg-green-500 text-white'
          }`}
        >
          {value ? 'Bloqueado' : 'Activo'}
        </span>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={users}
        actions={actions}
        pageSizeOptions={[5, 10, 20, 50]}
        headerButton={addButton}
        url="/user-management/users"
        paginator={paginator}
        searchComponent={
          <SearchComponent onSearch={handlerSearchByType} pageSize={pageSize} />
        }
        onPageSizeChange={updatePageSize}
      />

      {/* Modal para activar/desactivar usuario */}
      <ActivationModal
        isOpen={isActivationModalOpen}
        title={
          selectedUser?.status ? '¿Desactivar Usuario?' : '¿Activar Usuario?'
        }
        message={
          selectedUser?.status
            ? '¿Estás seguro de que deseas desactivar este usuario?'
            : '¿Estás seguro de que deseas activar este usuario?'
        }
        confirmText={selectedUser?.status ? 'Desactivar' : 'Activar'}
        cancelText="Cancelar"
        onConfirm={handleConfirmToggleStatus}
        onCancel={handleCancelToggle}
      />

      {/* Modal para registrar usuario */}
      <UserRegistrationPopup
        popupOpen={isCreateModalOpen}
        onClose={() => handleCloseCreate()}
        onRegister={handlerAddUser}
      />

      {/* Modal para editar usuario */}
      <UserEditPopup
        popupOpen={isUpdateModalOpen}
        onClose={() => handleCloseUpdate()}
        onUpdate={handleUpdateUser}
      />
    </>
  );
};

export default UserTable;
